import { useEffect } from 'react';
import { useForm, useWatch, Controller } from 'react-hook-form';
import { toast } from 'sonner';
import { createRatePeriodSchema, type CreateRatePeriodInput } from '@pms/shared';
import { useCreateRatePeriod, useUpdateRatePeriod } from '@/hooks/payroll/useRatePeriodMutations';
import { RateApiError, type RatePeriod } from '@/services/rateApi.service';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const CURRENT_YEAR = new Date().getFullYear();
const YEARS = Array.from({ length: 9 }, (_, i) => CURRENT_YEAR - 6 + i);

const monthOf = (key: string) => Number(key.slice(5, 7));
const yearOf = (key: string) => Number(key.slice(0, 4));
const buildKey = (year: number, month: number) => `${year}-${String(month).padStart(2, '0')}`;

/**
 * An overlap comes back as a 409 whose message names the period it collided with
 * — the one thing that makes it fixable — so it is passed through as-is.
 */
function getErrorMessage(e: unknown): string {
  return e instanceof RateApiError ? e.message : 'Something went wrong';
}

/** An open end reaches forever; comparing YYYY-MM as text orders it correctly. */
const OPEN_END = '9999-12';

/** "September 2026" for a YYYY-MM key, so a clash reads the way the form does. */
const labelOf = (key: string) => `${MONTHS[monthOf(key) - 1]} ${yearOf(key)}`;

/**
 * The first existing period a candidate range would collide with, or null.
 *
 * The API rejects an overlap with a 409, but that answer arrives after the write
 * is attempted and is worded by the external service — including the case that
 * kept biting here, an open-ended period silently blocking every later month.
 * Finding the clash locally lets the form name it in the user's own terms and say
 * what to do about it, and costs nothing: the table on the page behind this modal
 * is already holding the periods.
 */
function findOverlap(
  periods: RatePeriod[],
  from: string,
  to: string | null,
  ignoreRateId?: number,
): RatePeriod | null {
  const end = to ?? OPEN_END;
  return (
    periods.find((p) => {
      if (p.rate_id === ignoreRateId) return false;
      return from <= (p.effective_to ?? OPEN_END) && p.effective_from <= end;
    }) ?? null
  );
}

/** Form state keeps month and year apart so each gets its own Select. */
interface FormValues {
  rate: number;
  fromMonth: number;
  fromYear: number;
  toMonth: number;
  toYear: number;
  openEnded: boolean;
}

interface Props {
  userId: string;
  employeeName: string;
  /** The period being edited; omit to add a new one. */
  period?: RatePeriod | null;
  /** Every period this employee already has, used to catch a clash before saving. */
  periods: RatePeriod[];
  open: boolean;
  onClose: () => void;
}

export function RatePeriodModal({ userId, employeeName, period, periods, open, onClose }: Props) {
  const isEdit = !!period;
  const create = useCreateRatePeriod(userId, employeeName);
  const update = useUpdateRatePeriod(userId);
  const pending = create.isPending || update.isPending;

  const {
    register,
    control,
    handleSubmit,
    reset,
    setError,
    formState: { errors },
  } = useForm<FormValues>();

  // useWatch rather than watch(): it is memo-safe, so the component still
  // compiles under the React Compiler.
  const openEnded = useWatch({ control, name: 'openEnded' });

  // Reload whenever a different period opens, so one period's edits never leak
  // into the next and an add always starts clean.
  useEffect(() => {
    if (!open) return;

    if (period) {
      const from = period.effective_from;
      const to = period.effective_to ?? from;
      reset({
        rate: Number(period.rate),
        fromMonth: monthOf(from),
        fromYear: yearOf(from),
        toMonth: monthOf(to),
        toYear: yearOf(to),
        openEnded: period.effective_to === null,
      });
      return;
    }

    const now = new Date();
    reset({
      rate: 0,
      fromMonth: now.getMonth() + 1,
      fromYear: now.getFullYear(),
      toMonth: now.getMonth() + 1,
      toYear: now.getFullYear(),
      openEnded: false,
    });
  }, [open, period, reset]);

  const onSubmit = (values: FormValues) => {
    const candidate = {
      rate: Number(values.rate),
      effective_from: buildKey(values.fromYear, values.fromMonth),
      effective_to: values.openEnded ? null : buildKey(values.toYear, values.toMonth),
    };

    // The shared schema is the authority on what the API accepts, so it validates
    // the recombined payload rather than the split month/year fields the form
    // happens to use. Its issues are mapped back onto whichever field produced
    // them; a period-level issue lands on the end month, which is the one the
    // user can move.
    const parsed = createRatePeriodSchema.safeParse(candidate);
    if (!parsed.success) {
      const issue = parsed.error.issues[0];
      const field: keyof FormValues = issue?.path[0] === 'rate' ? 'rate' : 'toMonth';
      setError(field, { message: issue?.message ?? 'Invalid rate period' });
      return;
    }
    const payload: CreateRatePeriodInput = parsed.data;

    // Editing a period may legitimately keep its own months, so it is excluded
    // from its own clash check.
    const clash = findOverlap(
      periods,
      payload.effective_from,
      payload.effective_to ?? null,
      period?.rate_id,
    );
    if (clash) {
      const span = clash.effective_to
        ? `${labelOf(clash.effective_from)} – ${labelOf(clash.effective_to)}`
        : `${labelOf(clash.effective_from)} onwards (open-ended)`;
      setError('toMonth', {
        message:
          `Overlaps an existing period: ₹${clash.rate}/hr, ${span}. ` +
          `Edit that period instead of adding a new one.`,
      });
      return;
    }

    const handlers = {
      onSuccess: () => {
        toast.success(isEdit ? 'Rate period updated' : 'Rate period added');
        onClose();
      },
      // A 409 carries the Rate API's own message, which names the period that was
      // hit — the only thing that makes an overlap fixable.
      onError: (e: unknown) => toast.error(getErrorMessage(e)),
    };

    if (isEdit && period) {
      update.mutate({ rateId: period.rate_id, input: payload }, handlers);
    } else {
      create.mutate(payload, handlers);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogTitle>{isEdit ? 'Edit Rate Period' : 'Add Rate Period'}</DialogTitle>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="rounded-md bg-muted px-3 py-2 text-xs">
            <span className="text-muted-foreground">Employee: </span>
            <span className="font-medium">{employeeName}</span>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="rate">Rate (₹/hr)</Label>
            <Input
              id="rate"
              type="number"
              step="0.01"
              min="0"
              autoFocus
              {...register('rate', { valueAsNumber: true })}
            />
            {errors.rate && <p className="text-xs text-destructive">{errors.rate.message}</p>}
          </div>

          <div className="space-y-1.5">
            <Label>Effective From</Label>
            <div className="grid grid-cols-2 gap-3">
              <Controller
                control={control}
                name="fromMonth"
                render={({ field }) => (
                  <Select value={String(field.value)} onValueChange={(v) => field.onChange(Number(v))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {MONTHS.map((label, i) => (
                        <SelectItem key={label} value={String(i + 1)}>{label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
              <Controller
                control={control}
                name="fromYear"
                render={({ field }) => (
                  <Select value={String(field.value)} onValueChange={(v) => field.onChange(Number(v))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {YEARS.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
                    </SelectContent>
                  </Select>
                )}
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <Label>Effective To</Label>
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">Open-ended</span>
                <Controller
                  control={control}
                  name="openEnded"
                  render={({ field }) => (
                    <Switch
                      checked={field.value}
                      onChange={field.onChange}
                      size="sm"
                      aria-label="Open-ended period"
                    />
                  )}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Controller
                control={control}
                name="toMonth"
                render={({ field }) => (
                  <Select
                    value={String(field.value)}
                    onValueChange={(v) => field.onChange(Number(v))}
                    disabled={openEnded}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {MONTHS.map((label, i) => (
                        <SelectItem key={label} value={String(i + 1)}>{label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
              <Controller
                control={control}
                name="toYear"
                render={({ field }) => (
                  <Select
                    value={String(field.value)}
                    onValueChange={(v) => field.onChange(Number(v))}
                    disabled={openEnded}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {YEARS.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
                    </SelectContent>
                  </Select>
                )}
              />
            </div>
            {errors.toMonth && (
              <p className="text-xs text-destructive">{errors.toMonth.message}</p>
            )}
            {openEnded && (
              <p className="text-xs text-muted-foreground">
                An open-ended period blocks any later one until its end month is set.
              </p>
            )}
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={pending}>
              {pending ? 'Saving…' : isEdit ? 'Save Changes' : 'Add Period'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
